import React from 'react';
import { FaLinkedinIn, FaCheck, FaLeaf, FaSeedling, FaTractor, FaChartLine, FaArrowRight, FaQuoteLeft } from 'react-icons/fa';
import { MdAgriculture, MdSupportAgent } from 'react-icons/md';

const PremiumKrishidnyaAbout = () => {
  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Krishidnya",
    "description": "Leading agritech company revolutionizing Indian agriculture through technology and innovation",
    "url": "https://krishidnya.com",
    "logo": "https://krishidnya.com/logo.png",
    "sameAs": [
      "https://linkedin.com/company/krishidnya",
      "https://twitter.com/krishidnya"
    ]
  };

  const companyData = {
    title: "About Krishidnya - Revolutionizing Agriculture Through Technology",
    description: "Krishidnya is India's leading agritech company transforming farming with AI-powered solutions, sustainable practices, and farmer-first technology since 2020.",
    intro: "Krishidnya is revolutionizing Indian agriculture through cutting-edge technology, sustainable practices, and farmer-centric innovation.",
    fullDescription: "As India's fastest-growing agritech platform, we combine traditional farming wisdom with modern technology to create sustainable, profitable, and resilient agricultural ecosystems. Our solutions empower farmers with data-driven insights, market access, and smart farming tools."
  };

 const achievements = [
  { number: "1000+", label: "Farmers Empowered", icon: <FaSeedling /> },
  { number: "75+", label: "Districts Covered", icon: <MdAgriculture /> },
  { number: "98%", label: "Success Rate", icon: <FaChartLine /> },
  { number: "45%+", label: "Yield Improvement", icon: <FaTractor /> }
];
  
  const coreValues = [
    { 
      icon: <FaLeaf />, 
      title: "Farmer First", 
      description: "Every decision prioritizes farmer welfare and prosperity",
      points: ["Direct Market Access", "Fair Pricing", "24/7 Support"]
    },
    { 
      icon: <MdAgriculture />, 
      title: "Sustainable Innovation", 
      description: "Eco-friendly technology solutions for sustainable farming",
      points: ["Water Conservation", "Organic Solutions", "Soil Health"]
    },
    { 
      icon: <FaChartLine />, 
      title: "Data-Driven Excellence", 
      description: "AI-powered insights for better agricultural decisions",
      points: ["Yield Prediction", "Market Analytics", "Risk Management"]
    },
    { 
      icon: <MdSupportAgent />, 
      title: "End-to-End Support", 
      description: "Complete farming solutions from seed to market",
      points: ["Expert Guidance", "Supply Chain", "Quality Assurance"]
    }
  ];

  const leadership = [
    { 
      name: "Tejas Barguje", 
      role: "Founder & CEO", 
      bio: "3+ years in agritech innovation. Led digital transformation for 1000+ farmers.",
      expertise: ["AgriTech Strategy", "Business Scaling", "Farmer Empowerment"],
      linkedin: "https://www.linkedin.com/in/tejas-cybersecurityanalyst-",
      color: "#2E8B57"
    },
    { 
      name: "Bhagyalaxmi Bamungi", 
      role: "Co-founder & CTO", 
      bio: "Expert in AI-driven farming solutions and agricultural robotics.",
      expertise: ["AI/ML", "IoT", "Precision Farming"],
      linkedin: "https://www.linkedin.com/in/bhagyalaxmi-bamungi-132685265",
      color: "#2E8B57"
    },
    { 
      name: "Rushikesh Pawar", 
      role: "Co-founder & COO", 
      bio: "Operations specialist scaling sustainable farming networks across India.",
      expertise: ["Operations", "Supply Chain", "Logistics"],
      linkedin: "https://www.linkedin.com/in/rushikesh-pawar-8b5802248",
      color: "#2E8B57"
    },
    { 
      name: "Abhijit Abuj", 
      role: "Co-founder & CMO ", 
      bio: "Strategic marketer connecting farmers with premium global markets.",
      expertise: ["Market Strategy", "Brand Building", "Farmer Outreach"],
      linkedin: "#",
      color: "#2E8B57"
    }
  ];

  const keyFeatures = [
    "AI-powered crop monitoring and yield prediction",
    "Direct market access eliminating middlemen",
    "Sustainable farming practices and training",
    "24/7 expert support via app and call center",
    "Weather-based advisory and risk management",
    "Supply chain optimization and logistics"
  ];

  const processSteps = [
    {
      step: "01",
      title: "Farmer Onboarding",
      description: "Digital registration and need assessment"
    },
    {
      step: "02",
      title: "Technology Setup",
      description: "AI tools and sensor installation"
    },
    {
      step: "03",
      title: "Data Analysis",
      description: "AI-driven insights and recommendations"
    },
    {
      step: "04",
      title: "Implementation",
      description: "Smart farming practices execution"
    },
    {
      step: "05",
      title: "Market Access",
      description: "Direct connection to buyers"
    },
    {
      step: "06",
      title: "Continuous Support",
      description: "24/7 monitoring and assistance"
    }
  ];

  return (
    <div className="premium-krishidnya-about">
      {/* SEO Meta Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Hero Section */}
      <header className="hero-section" role="banner">
        <div className="container">
          <nav className="breadcrumb" aria-label="Breadcrumb">
           
          </nav>
          
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="title-part-1">Transforming Agriculture</span>
              <span className="title-part-2">Through Innovation</span>
            </h1>
            
            <div className="hero-description">
              <p className="lead">{companyData.intro}</p>
              <p>{companyData.fullDescription}</p>
            </div>
            
            <div className="hero-cta">
              <a href="/contact" className="btn-primary">
                <span>Start Your Journey</span>
                <FaArrowRight className="btn-icon" />
              </a>
              <a href="/demo" className="btn-secondary">
                View Demo
              </a>
            </div>
            
            <div className="achievements-grid" role="list">
              {achievements.map((achievement, index) => (
                <div className="achievement-card" key={index} role="listitem">
                  <div className="achievement-icon" aria-hidden="true">
                    {achievement.icon}
                  </div>
                  <div className="achievement-content">
                    <div className="achievement-number">{achievement.number}</div>
                    <div className="achievement-label">{achievement.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Mission & Vision */}
      <section className="mission-section" aria-labelledby="mission-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="mission-heading">Our Purpose & Vision</h2>
            <p className="section-description">Guiding principles that drive our agricultural revolution</p>
          </div>
          
          <div className="mission-cards">
            <article className="mission-card">
              <div className="card-header">
                <div className="card-icon">
                  <FaCheck />
                </div>
                <h3>Our Mission</h3>
              </div>
              <p>Empower every farmer with accessible technology, sustainable practices, and fair market access to maximize agricultural productivity and economic prosperity.</p>
              <ul className="mission-points">
                <li><FaCheck /> Technology Access for All Farmers</li>
                <li><FaCheck /> Sustainable Farming Practices</li>
                <li><FaCheck /> Market Connectivity & Fair Pricing</li>
              </ul>
            </article>
            
            <article className="mission-card vision">
              <div className="card-header">
                <div className="card-icon">
                  <FaLeaf />
                </div>
                <h3>Our Vision</h3>
              </div>
              <p>Create India's most trusted agritech ecosystem where traditional farming wisdom and modern technology converge for sustainable, profitable agriculture.</p>
              <ul className="mission-points">
                <li><FaCheck /> Technology-Enhanced Traditional Farming</li>
                <li><FaCheck /> Prosperous Farming Communities</li>
                <li><FaCheck /> Sustainable National Food Security</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="values-section" aria-labelledby="values-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="values-heading">Our Core Values</h2>
            <p className="section-description">Principles that define our approach to agricultural transformation</p>
          </div>
          
          <div className="values-grid">
            {coreValues.map((value, index) => (
              <article className="value-card" key={index}>
                <div className="value-header">
                  <div className="value-icon" aria-hidden="true">
                    {value.icon}
                  </div>
                  <div>
                    <h3>{value.title}</h3>
                    <p className="value-subtitle">{value.description}</p>
                  </div>
                </div>
                <ul className="value-points">
                  {value.points.map((point, pointIndex) => (
                    <li key={pointIndex}>
                      <FaCheck className="check-icon" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="process-section" aria-labelledby="process-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="process-heading">Our Working Process</h2>
            <p className="section-description">A systematic approach to transforming traditional farming</p>
          </div>
          
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div className="process-step" key={index}>
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="step-connector"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="features-section" aria-labelledby="features-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="features-heading">Comprehensive Farming Solutions</h2>
            <p className="section-description">End-to-end technology solutions for modern agriculture</p>
          </div>
          
          <div className="features-grid">
            {keyFeatures.map((feature, index) => (
              <div className="feature-item" key={index}>
                <div className="feature-check" aria-hidden="true">
                  <FaCheck />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
          
          <div className="section-cta">
            <a href="/solutions" className="btn-secondary">
              Explore All Solutions
            </a>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="leadership-section" aria-labelledby="leadership-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="leadership-heading">Leadership Team</h2>
            <p className="section-description">Expert leaders driving agricultural innovation and transformation</p>
          </div>
          
          <div className="leadership-grid">
            {leadership.map((leader, index) => (
              <article className="leader-card" key={index}>
                <div className="leader-header">
                  <div className="leader-avatar" style={{ backgroundColor: leader.color }}>
                    {leader.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="leader-info">
                    <h3>{leader.name}</h3>
                    <div className="leader-role">{leader.role}</div>
                  </div>
                </div>
                
                <p className="leader-bio">{leader.bio}</p>
                
                <div className="leader-expertise">
                  {leader.expertise.map((skill, skillIndex) => (
                    <span key={skillIndex} className="expertise-tag">
                      {skill}
                    </span>
                  ))}
                </div>
                
                <a 
                  href={leader.linkedin} 
                  className="linkedin-link" 
                  aria-label={`Connect with ${leader.name} on LinkedIn`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaLinkedinIn />
                  <span>Connect</span>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Founder's Message */}
      <section className="founder-section" aria-labelledby="founder-heading">
        <div className="container">
          <div className="founder-content">
            <div className="quote-mark">
              <FaQuoteLeft />
            </div>
            <h2 id="founder-heading">Message from Our Founder</h2>
            
            <blockquote className="founder-message">
              <p>
                At Krishidnya, we believe that true agricultural transformation comes from empowering farmers with technology that respects their wisdom while enhancing their capabilities. 
                Our mission extends beyond profit—it's about building sustainable livelihoods and securing India's food future through innovation that truly matters to farmers.
              </p>
            </blockquote>
            
            <div className="founder-signature">
              <div className="founder-name">Tejas Barguje</div>
              <div className="founder-title">Founder & CEO, Krishidnya</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section" aria-labelledby="cta-heading">
        <div className="container">
          <div className="cta-content">
            <h2 id="cta-heading">Ready to Transform Your Farming?</h2>
            <p>Join thousands of farmers who have already revolutionized their agriculture with Krishidnya technology.</p>
            
            <div className="cta-features">
              <div className="feature">
                <FaCheck /> <span>Free Consultation</span>
              </div>
              <div className="feature">
                <FaCheck /> <span>30-Day Trial</span>
              </div>
              <div className="feature">
                <FaCheck /> <span>Expert Support</span>
              </div>
            </div>
            
            <div className="cta-buttons">
              <a href="/contact" className="btn-primary">
                Get Started Today
              </a>
              <a href="/demo" className="btn-secondary">
                Schedule a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Font Import */
        @import url('https://fonts.googleapis.com/css2?family=Source+Serif+Pro:wght@400;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

        /* Premium Design System - Black with Green Accents */
        :root {
          --primary-black: #000000;
          --secondary-black: #1A1A1A;
          --medium-black: #333333;
          --light-black: #666666;
          --primary-green: #2E8B57; /* Medium green - primary accent */
          --secondary-green: #3A7D34; /* Slightly darker green for secondary */
          --light-green: #4CAF7A; /* Lighter green for highlights */
          --accent-green: #2D5A27; /* Dark green for special accents */
          --white: #FFFFFF;
          --off-white: #FAFAFA;
          --light-gray: #F5F5F5;
          --medium-gray: #E8E8E8;
          --border-color: #E0E0E0;
          --text-primary: #000000;
          --text-secondary: #333333;
          --text-light: #666666;
          --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
          --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
          --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
          --radius-sm: 6px;
          --radius-md: 10px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          --transition: all 0.3s ease;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .premium-krishidnya-about {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: var(--text-primary);
          background: var(--white);
          line-height: 1.6;
          overflow-x: hidden;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Typography */
        h1, h2, h3, h4 {
          font-family: 'Source Serif Pro', serif;
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: -0.01em;
          color: var(--primary-black);
        }

        .hero-title {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .title-part-1 {
          font-size: 3.5rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--primary-black);
          line-height: 1.1;
        }

        .title-part-2 {
          font-size: 3.5rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--primary-green);
          line-height: 1.1;
        }

        h2 { 
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }

        h3 { 
          font-size: 1.75rem;
          margin-bottom: 0.75rem;
        }

        h4 { 
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
        }

        p { 
          font-size: 1.125rem;
          color: var(--text-secondary);
          line-height: 1.7;
          font-weight: 400;
        }

        .lead {
          font-size: 1.25rem;
          font-weight: 500;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .section-description {
          font-size: 1.125rem;
          color: var(--text-light);
          margin-top: 0.5rem;
          font-weight: 400;
        }

        /* Buttons */
        .btn-primary, .btn-secondary {
          padding: 14px 32px;
          font-size: 1rem;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          border-radius: var(--radius-md);
          text-decoration: none;
          transition: var(--transition);
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .btn-primary {
          background: var(--primary-black);
          color: var(--white);
          border: 2px solid var(--primary-black);
        }

        .btn-primary:hover {
          background: var(--secondary-black);
          border-color: var(--secondary-black);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .btn-secondary {
          background: transparent;
          color: var(--primary-black);
          border: 2px solid var(--primary-black);
        }

        .btn-secondary:hover {
          background: var(--primary-black);
          color: var(--white);
          transform: translateY(-2px);
        }

        .btn-icon {
          font-size: 1.125rem;
          transition: transform 0.3s ease;
        }

        .btn-primary:hover .btn-icon {
          transform: translateX(4px);
        }

        /* Hero Section */
        .hero-section {
          padding: 100px 0 80px;
          background: var(--white);
          position: relative;
        }

        .hero-section::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 4px;
          background: var(--primary-green);
          border-radius: 2px;
        }

        .breadcrumb {
          margin-bottom: 2rem;
        }

        .breadcrumb ol {
          display: flex;
          list-style: none;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-family: 'Inter', sans-serif;
        }

        .breadcrumb a {
          color: var(--text-light);
          text-decoration: none;
          font-weight: 500;
          transition: var(--transition);
        }

        .breadcrumb a:hover {
          color: var(--primary-green);
        }

        .breadcrumb .current {
          color: var(--primary-green);
          font-weight: 600;
        }

        .hero-content {
          max-width: 800px;
          text-align: center;
          margin: 0 auto;
        }

        .hero-title {
          margin-bottom: 1.5rem;
          line-height: 1.1;
        }

        .hero-description {
          margin-bottom: 3rem;
        }

        .hero-description p {
          margin-bottom: 1.25rem;
        }

        .hero-description p:last-child {
          margin-bottom: 0;
        }

        .hero-cta {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 4rem;
          flex-wrap: wrap;
        }

        /* Achievements Grid */
        .achievements-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .achievement-card {
          padding: 2rem 1.5rem;
          background: var(--off-white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          text-align: center;
          transition: var(--transition);
        }

        .achievement-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-lg);
          border-color: var(--primary-green);
        }

        .achievement-icon {
          font-size: 2.5rem;
          color: var(--primary-green);
          margin-bottom: 1rem;
        }

        .achievement-number {
          font-size: 2rem;
          font-weight: 700;
          color: var(--primary-black);
          margin-bottom: 0.5rem;
          font-family: 'Source Serif Pro', serif;
        }

        .achievement-label {
          font-size: 0.875rem;
          color: var(--text-light);
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 500;
        }

        /* Mission Section */
        .mission-section {
          padding: 100px 0;
          background: var(--light-gray);
        }

        .section-intro {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 3rem;
        }

        .mission-cards {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .mission-card {
          padding: 3rem 2.5rem;
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          transition: var(--transition);
        }

        .mission-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--shadow-lg);
          border-color: var(--primary-green);
        }

        .vision {
          background: var(--off-white);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .card-icon {
          width: 56px;
          height: 56px;
          border-radius: var(--radius-md);
          background: var(--off-white);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: var(--primary-green);
          flex-shrink: 0;
        }

        .mission-points {
          list-style: none;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border-color);
        }

        .mission-points li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          font-size: 1rem;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .mission-points li:last-child {
          margin-bottom: 0;
        }

        .mission-points li svg {
          color: var(--primary-green);
          flex-shrink: 0;
        }

        /* Values Section */
        .values-section {
          padding: 100px 0;
          background: var(--white);
        }

        .values-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .value-card {
          padding: 2.5rem 2rem;
          background: var(--off-white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          transition: var(--transition);
        }

        .value-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .value-header {
          display: flex;
          align-items: flex-start;
          gap: 1.25rem;
          margin-bottom: 1.25rem;
        }

        .value-icon {
          width: 56px;
          height: 56px;
          border-radius: var(--radius-md);
          background: var(--white);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: var(--primary-green);
          flex-shrink: 0;
          border: 1px solid var(--border-color);
        }

        .value-subtitle {
          font-size: 0.95rem;
          color: var(--text-light);
          margin-top: 0.25rem;
        }

        .value-points {
          list-style: none;
          padding-left: 0;
        }

        .value-points li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
          color: var(--text-light);
        }

        .value-points li:last-child {
          margin-bottom: 0;
        }

        .check-icon {
          color: var(--primary-green);
          font-size: 0.875rem;
          flex-shrink: 0;
        }

        /* Process Section */
        .process-section {
          padding: 100px 0;
          background: var(--light-gray);
        }

        .process-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
        }

        .process-step {
          position: relative;
          text-align: center;
          padding: 2rem;
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          transition: var(--transition);
        }

        .process-step:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .step-number {
          font-size: 3rem;
          font-weight: 700;
          color: var(--primary-green);
          opacity: 0.2;
          margin-bottom: 1rem;
          font-family: 'Source Serif Pro', serif;
        }

        .step-content h4 {
          margin-bottom: 0.5rem;
          color: var(--primary-black);
        }

        .step-content p {
          font-size: 0.95rem;
          color: var(--text-light);
          line-height: 1.6;
        }

        .step-connector {
          position: absolute;
          top: 50%;
          right: -1rem;
          width: 2rem;
          height: 2px;
          background: var(--border-color);
          display: none;
        }

        /* Features Section */
        .features-section {
          padding: 100px 0;
          background: var(--white);
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          max-width: 800px;
          margin: 0 auto 2.5rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
          background: var(--off-white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          transition: var(--transition);
        }

        .feature-item:hover {
          transform: translateX(4px);
          border-color: var(--primary-green);
          background: var(--white);
        }

        .feature-check {
          color: var(--primary-green);
          font-size: 1.125rem;
          flex-shrink: 0;
        }

        .feature-item span {
          color: var(--text-secondary);
          font-weight: 500;
        }

        .section-cta {
          text-align: center;
        }

        /* Leadership Section */
        .leadership-section {
          padding: 100px 0;
          background: var(--light-gray);
        }

        .leadership-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .leader-card {
          padding: 2.5rem 2rem;
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          transition: var(--transition);
        }

        .leader-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--shadow-lg);
          border-color: var(--primary-green);
        }

        .leader-header {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          margin-bottom: 1.25rem;
        }

        .leader-avatar {
          width: 64px;
          height: 64px;
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--white);
          flex-shrink: 0;
        }

        .leader-info h3 {
          margin-bottom: 0.25rem;
          color: var(--primary-black);
        }

        .leader-role {
          font-size: 0.875rem;
          color: var(--primary-green);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .leader-bio {
          font-size: 1rem;
          line-height: 1.6;
          margin-bottom: 1.25rem;
          color: var(--text-secondary);
        }

        .leader-expertise {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .expertise-tag {
          padding: 0.5rem 1rem;
          background: var(--off-white);
          border-radius: 20px;
          font-size: 0.875rem;
          color: var(--text-light);
          font-weight: 500;
        }

        .linkedin-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: var(--off-white);
          color: var(--text-secondary);
          text-decoration: none;
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
          font-weight: 600;
          transition: var(--transition);
          border: 1px solid var(--border-color);
        }

        .linkedin-link:hover {
          background: #0077B5;
          color: var(--white);
          border-color: #0077B5;
        }

        /* Founder Section */
        .founder-section {
          padding: 100px 0;
          background: var(--white);
        }

        .founder-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
          padding: 4rem;
          background: var(--off-white);
          border-radius: var(--radius-xl);
          border: 1px solid var(--border-color);
          position: relative;
        }

        .quote-mark {
          font-size: 4rem;
          color: var(--primary-green);
          opacity: 0.1;
          margin-bottom: 1rem;
        }

        .founder-content h2 {
          margin-bottom: 2rem;
        }

        .founder-message {
          margin: 0;
          padding: 0;
          border: none;
        }

        .founder-message p {
          font-size: 1.25rem;
          line-height: 1.8;
          color: var(--text-primary);
          font-style: italic;
          margin-bottom: 3rem;
        }

        .founder-signature {
          padding-top: 2rem;
          border-top: 1px solid var(--border-color);
        }

        .founder-name {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--primary-black);
          margin-bottom: 0.25rem;
        }

        .founder-title {
          font-size: 0.875rem;
          color: var(--text-light);
          font-weight: 500;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 0;
          background: var(--light-gray);
        }

        .cta-content {
          text-align: center;
          max-width: 700px;
          margin: 0 auto;
        }

        .cta-content h2 {
          margin-bottom: 1rem;
        }

        .cta-content p {
          font-size: 1.125rem;
          margin-bottom: 2.5rem;
          color: var(--text-secondary);
        }

        .cta-features {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-bottom: 2.5rem;
          flex-wrap: wrap;
        }

        .cta-features .feature {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--primary-green);
          font-weight: 500;
        }

        .cta-features .feature svg {
          color: var(--primary-green);
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .container {
            padding: 0 32px;
          }

          .title-part-1,
          .title-part-2 {
            font-size: 3rem;
          }
          
          h2 { 
            font-size: 2.25rem;
          }
          
          .achievements-grid,
          .mission-cards,
          .values-grid,
          .leadership-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .process-steps {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .step-connector {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .container {
            padding: 0 24px;
          }

          .title-part-1,
          .title-part-2 {
            font-size: 2.5rem;
          }
          
          h2 { 
            font-size: 2rem;
          }
          
          h3 { 
            font-size: 1.5rem;
          }
          
          p, .lead {
            font-size: 1.063rem;
          }

          .hero-section {
            padding: 80px 0 60px;
          }
          
          .mission-section,
          .values-section,
          .process-section,
          .features-section,
          .leadership-section,
          .founder-section,
          .cta-section {
            padding: 80px 0;
          }
          
          .hero-cta {
            flex-direction: column;
            align-items: center;
          }
          
          .btn-primary, .btn-secondary {
            width: 100%;
            max-width: 300px;
          }
          
          .achievement-card {
            padding: 1.5rem 1rem;
          }
          
          .process-steps {
            grid-template-columns: 1fr;
          }
          
          .features-grid {
            grid-template-columns: 1fr;
          }
          
          .founder-content {
            padding: 2.5rem 1.5rem;
          }
          
          .founder-message p {
            font-size: 1.125rem;
          }
          
          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 20px;
          }

          .title-part-1,
          .title-part-2 {
            font-size: 2.25rem;
          }
          
          h2 { 
            font-size: 1.75rem;
          }
          
          h3 { 
            font-size: 1.25rem;
          }
          
          .hero-section {
            padding: 60px 0 40px;
          }
          
          .mission-section,
          .values-section,
          .process-section,
          .features-section,
          .leadership-section,
          .founder-section,
          .cta-section {
            padding: 60px 0;
          }
          
          .achievements-grid {
            gap: 1rem;
          }
          
          .mission-card,
          .value-card,
          .leader-card {
            padding: 1.5rem 1.25rem;
          }
          
          .card-header,
          .value-header,
          .leader-header {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }
          
          .founder-message p {
            font-size: 1rem;
          }
          
          .cta-features {
            flex-direction: column;
            gap: 1rem;
          }
        }

        /* Print Styles */
        @media print {
          .premium-krishidnya-about {
            color: #000;
            background: #fff;
          }
          
          .hero-section,
          .mission-section,
          .values-section,
          .process-section,
          .features-section,
          .leadership-section,
          .founder-section,
          .cta-section {
            padding: 40px 0;
            break-inside: avoid;
          }
          
          .cta-section {
            page-break-before: always;
          }
        }
      `}</style>
    </div>
  );
};

export default PremiumKrishidnyaAbout;
